import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Clock } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'

interface TimePickerProps {
  value: string        // HH:MM or ''
  onChange: (val: string) => void
  label?: string
  error?: string
  minuteStep?: number  // default 15
  isHourDisabled?: (hour: number) => boolean
}

const pad = (n: number) => String(n).padStart(2, '0')

function parseTime(value: string): [number, number] {
  if (!value) return [12, 0]
  const [h, m] = value.split(':').map(Number)
  return [isNaN(h) ? 12 : h, isNaN(m) ? 0 : m]
}

export function TimePicker({ value, onChange, label, error, minuteStep = 15, isHourDisabled }: TimePickerProps) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0, width: 140 })
  const buttonRef = useRef<HTMLButtonElement>(null)
  const hourListRef = useRef<HTMLDivElement>(null)
  const minuteListRef = useRef<HTMLDivElement>(null)

  const [selHour, selMin] = parseTime(value)
  const MINUTES = Array.from({ length: Math.floor(60 / minuteStep) }, (_, i) => i * minuteStep)
  const HOURS = Array.from({ length: 24 }, (_, i) => i)

  const updatePos = useCallback(() => {
    if (!buttonRef.current) return
    const r = buttonRef.current.getBoundingClientRect()
    const viewportPad = 8
    const viewportWidth = window.visualViewport?.width ?? window.innerWidth
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight
    const pickerWidth = Math.min(140, viewportWidth - viewportPad * 2)
    const pickerHeight = Math.min(250, viewportHeight - viewportPad * 2)
    const left = Math.min(Math.max(r.left, viewportPad), viewportWidth - pickerWidth - viewportPad)
    const below = r.bottom + 6
    const top = below + pickerHeight <= viewportHeight - viewportPad
      ? below
      : Math.max(viewportPad, r.top - pickerHeight - 6)
    setPos({ top, left, width: pickerWidth })
  }, [])

  const handleOpen = () => {
    updatePos()
    setOpen((o) => !o)
  }

  // Close on outside click or scroll
  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onScroll = () => updatePos()
    document.addEventListener('mousedown', close)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    window.visualViewport?.addEventListener('resize', onScroll)
    return () => {
      document.removeEventListener('mousedown', close)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
      window.visualViewport?.removeEventListener('resize', onScroll)
    }
  }, [open, updatePos])

  // Scroll selected items into view when opened
  useEffect(() => {
    if (!open) return
    const ITEM_H = 36
    const t = setTimeout(() => {
      if (hourListRef.current) {
        hourListRef.current.scrollTop = Math.max(0, selHour * ITEM_H - hourListRef.current.clientHeight / 2 + ITEM_H / 2)
      }
      if (minuteListRef.current) {
        const minIdx = MINUTES.indexOf(selMin)
        if (minIdx >= 0) {
          minuteListRef.current.scrollTop = Math.max(0, minIdx * ITEM_H - minuteListRef.current.clientHeight / 2 + ITEM_H / 2)
        }
      }
    }, 50)
    return () => clearTimeout(t)
  }, [open])

  const setHour = (h: number) => onChange(`${pad(h)}:${pad(selMin)}`)
  const setMinute = (m: number) => { onChange(`${pad(selHour)}:${pad(m)}`); setOpen(false) }

  const display = value ? `${pad(selHour)}:${pad(selMin)}` : null

  const picker = (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -6, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.97 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999, width: pos.width, maxHeight: 'calc(100dvh - 1rem)' }}
          className="overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] shadow-2xl select-none"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Live preview header */}
          <div className="flex items-center justify-center gap-1 py-2 border-b border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)]">
            <span className="text-base font-bold text-[var(--color-text-primary)] tabular-nums w-8 text-center">
              {pad(selHour)}
            </span>
            <span className="text-[var(--color-accent-amber)] font-bold">:</span>
            <span className="text-base font-bold text-[var(--color-text-primary)] tabular-nums w-8 text-center">
              {pad(selMin)}
            </span>
          </div>

          {/* Two-column scroll */}
          <div className="flex divide-x divide-[var(--color-border-subtle)]" style={{ height: 180 }}>
            {/* Hours */}
            <div ref={hourListRef} className="flex-1 overflow-y-auto">
              {HOURS.map((h) => {
                const disabled = isHourDisabled?.(h) ?? false
                return (
                  <button
                    key={h}
                    type="button"
                    onClick={() => !disabled && setHour(h)}
                    disabled={disabled}
                    className={`w-full h-9 flex items-center justify-center text-sm font-medium tabular-nums transition-colors ${
                      disabled
                        ? 'text-[var(--color-text-muted)]/30 cursor-not-allowed'
                        : h === selHour
                        ? 'bg-[var(--color-accent-amber)] text-black font-bold'
                        : 'text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)]'
                    }`}
                  >
                    {pad(h)}
                  </button>
                )
              })}
            </div>

            {/* Minutes */}
            <div ref={minuteListRef} className="flex-1 overflow-y-auto">
              {MINUTES.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMinute(m)}
                  className={`w-full h-9 flex items-center justify-center text-sm font-medium tabular-nums transition-colors ${
                    m === selMin
                      ? 'bg-[var(--color-accent-amber)] text-black font-bold'
                      : 'text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)]'
                  }`}
                >
                  :{pad(m)}
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )

  return (
    <div className="space-y-1.5">
      {label && (
        <label className="block text-sm font-medium text-[var(--color-text-secondary)]">{label}</label>
      )}
      <button
        ref={buttonRef}
        type="button"
        onClick={handleOpen}
        className={`flex h-11 w-full items-center gap-2 rounded-[var(--radius-md)] border bg-[var(--color-bg-elevated)] px-3 text-left text-sm transition-colors sm:h-10 ${
          error
            ? 'border-[var(--color-accent-red)]'
            : open
            ? 'border-[var(--color-accent-amber)]/60 ring-1 ring-[var(--color-accent-amber)]/30'
            : 'border-[var(--color-border-default)] hover:border-[var(--color-border-subtle)]'
        }`}
      >
        <Clock className="h-4 w-4 shrink-0 text-[var(--color-text-muted)]" />
        <span className={display ? 'text-[var(--color-text-primary)] font-medium tabular-nums' : 'text-[var(--color-text-muted)]'}>
          {display ?? 'Select time'}
        </span>
      </button>
      {error && <p className="text-xs text-[var(--color-accent-red)]">{error}</p>}

      {createPortal(picker, document.body)}
    </div>
  )
}
